import React, { useContext } from 'react';
import clsx from 'clsx';
import { useHover } from 'react-aria/useHover';
import { isFocusVisible } from 'react-aria/private/interactions/useFocusVisible';
import { useFocusRing } from 'react-aria/useFocusRing';
import { useOption } from 'react-aria/useListBox';
import { mergeProps } from 'react-aria/mergeProps';
import { Node } from '@react-types/shared';

import styles from '@danske/sapphire-css/components/listbox/listbox.module.css';
import avatarStyles from '@danske/sapphire-css/components/avatar/avatar.module.css';
import { Checkmark } from '@danske/sapphire-icons/react';

import { Icon } from '../../Icon';
import { usePreventTouchEnd } from '../../utils';
import { ListBoxContext } from './ListBoxContext';
import { tokens } from '../../SapphireProvider';

interface OptionProps<T> {
  item: Node<T>;
}

export const ListBoxOption = <T extends object>({
  item,
}: OptionProps<T>): JSX.Element => {
  const ref = React.useRef<HTMLDivElement>(null);
  const {
    state,
    shouldUseVirtualFocus,
    disableSelectedStyles,
    containerWidth,
    constrainWidth,
  } = useContext(ListBoxContext)!;
  const {
    optionProps,
    isDisabled,
    isFocused: isVirtuallyFocused,
    isPressed,
  } = useOption({ key: item.key }, state, ref);

  const { focusProps, isFocusVisible: isFocusRingVisible } = useFocusRing();
  const { hoverProps, isHovered } = useHover({ isDisabled });

  usePreventTouchEnd(ref);

  const rightIcon = item.props.rightIcon || item.props.icon;

  return (
    <div
      {...mergeProps(
        optionProps,
        hoverProps,
        shouldUseVirtualFocus ? {} : focusProps
      )}
      ref={ref}
      className={clsx(
        styles['sapphire-listbox__item'],
        styles['js-hover'],
        styles['js-focus'],
        {
          [styles['is-disabled']]: isDisabled,
          [styles['is-focus']]: shouldUseVirtualFocus
            ? isVirtuallyFocused && isFocusVisible()
            : isFocusRingVisible,
          [styles['is-hover']]: isHovered,
          [styles['is-active']]: isPressed,
        }
      )}
    >
      <div
        className={clsx(styles['sapphire-listbox__content'])}
        style={
          constrainWidth
            ? {
                maxWidth: `calc(${containerWidth}px - 2 * ${tokens.size.iconSm})`,
              }
            : undefined
        }
      >
        {!disableSelectedStyles &&
          state.selectionManager.selectedKeys.has(item.key) && (
            <div className={clsx(styles['sapphire-listbox__checkmark'])}>
              <Icon size="sm" color="informative">
                <Checkmark />
              </Icon>
            </div>
          )}
        {item.props.leftIcon && (
          <div className={clsx(styles['sapphire-listbox__icon'])}>
            <Icon size="sm">{item.props.leftIcon}</Icon>
          </div>
        )}
        <div className={clsx(styles['sapphire-listbox__text-container'])}>
          <div className={clsx(styles['sapphire-listbox__primary-text'])}>
            {item.rendered}
          </div>
          {item.props.secondaryText && (
            <div className={clsx(styles['sapphire-listbox__secondary-text'])}>
              {item.props.secondaryText}
            </div>
          )}
        </div>
        {rightIcon ? (
          <div className={clsx(styles['sapphire-listbox__icon'])}>
            <Icon size="sm">{rightIcon}</Icon>
          </div>
        ) : (
          item.props.avatar && (
            <div className={clsx(styles['sapphire-listbox__icon'])}>
              <div
                className={clsx(
                  avatarStyles['sapphire-avatar'],
                  avatarStyles['sapphire-avatar--sm']
                )}
              >
                {item.props.avatar}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};
